import React, { useState } from "react";
import { FileText, ChevronRight, Loader2 } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import LensCard from "@/components/patientdocs/pdLens";
import { getAnalysis, getFileContent } from "@/lib/luminaDocs";
import "@/components/patientdocs/pd.css";

// Encounter review → "Documents": two views (by type, like the chart; most recent 5) and a right-side viewer
// that loads the ingested analysis + the original file, the same calls the chart's document viewer makes.
const INK = "#141412", FAINT = "#9B9B97", SUB = "#6C6C69", HAIR = "#EFEFF1";
const day = (ts) => (ts ? new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "");

function Row({ x, onOpen }) {
  return (
    <button type="button" onClick={() => onOpen(x)} className="w-full flex items-start gap-2 text-left rounded-[8px] -mx-2 px-2 py-1.5 hover:bg-[#F1F1F4]" style={{ width: "calc(100% + 16px)" }} data-testid={`er-otc-doc-${x.key}`}>
      <FileText size={14} className="flex-none mt-[2px]" style={{ color: SUB }} />
      <span className="min-w-0 flex-1"><span className="block truncate text-[13px]" style={{ color: INK }} title={x.name}>{x.name}</span><span className="block text-[11.5px]" style={{ color: FAINT }}>{[x.type, x.from, day(x.ts)].filter(Boolean).join(" · ")}</span></span>
    </button>
  );
}

function Viewer({ doc, onClose }) {
  const [st, setSt] = useState(null);
  React.useEffect(() => {
    if (!doc) { setSt(null); return undefined; }
    let alive = true;
    setSt({ loading: true });
    (async () => {
      let a = null, dataUrl = "", ct = "";
      try { a = await getAnalysis(doc.analysisId); } catch { /* original may still load */ }
      const fid = a?.document_id || doc.documentId;
      if (fid) { try { const f = await getFileContent(fid); ct = f.content_type || "application/pdf"; const b64 = f.file_content || f.base64 || ""; if (b64) dataUrl = `data:${ct};base64,${b64}`; } catch { /* no original */ } }
      if (alive) setSt({ analysis: a, dataUrl, ct, error: !a && !dataUrl ? "Could not load this document." : null });
    })();
    return () => { alive = false; };
  }, [doc]);
  return (
    <Sheet open={!!doc} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="w-[94vw] sm:max-w-[760px] overflow-y-auto" data-testid="er-doc-sheet">
        {doc ? (
          <>
            <SheetHeader>
              <SheetTitle className="text-[15px] break-words pr-6">{doc.name}</SheetTitle>
              <SheetDescription className="text-[12px]">{[doc.type, day(doc.ts)].filter(Boolean).join(" · ")}</SheetDescription>
            </SheetHeader>
            <div className="mt-3 pd-scope">
              {st?.loading ? <div className="text-[12.5px] inline-flex items-center gap-2" style={{ color: FAINT }}><Loader2 size={13} className="animate-spin" />Loading the document…</div>
                : st?.error ? <div className="text-[12.5px]" style={{ color: "#C4302A" }}>{st.error}</div>
                : <>
                  {st?.analysis ? <LensCard analysis={st.analysis} /> : <div className="text-[12.5px]" style={{ color: FAINT }}>No ingested data for this file.</div>}
                  <div className="mt-4 text-[11.5px] font-semibold uppercase tracking-wide" style={{ color: FAINT }}>Original</div>
                  {!st?.dataUrl ? <div className="text-[12.5px] mt-1" style={{ color: FAINT }}>Original not available.</div>
                    : st.ct.includes("pdf") ? <iframe title="document" src={st.dataUrl} className="w-full mt-1 rounded-[8px]" style={{ height: "70vh", border: `1px solid ${HAIR}` }} data-testid="er-doc-original" />
                    : <img alt={doc.name} src={st.dataUrl} className="max-w-full mt-1 rounded-[8px]" data-testid="er-doc-original" />}
                </>}
            </div>
          </>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}

// Collapsed → the 3 most recent documents, one line each. Expanded ("Show all N") → "By type" groups.
export function DocList({ d, docs }) {
  const [all, setAll] = useState(false), [open, setOpen] = useState(() => new Set()), [doc, setDoc] = useState(null);
  const onOpen = (x) => (x.chart ? setDoc(x) : x.open());
  const groups = Object.entries(docs.reduce((m, x) => ((m[x.type] = m[x.type] || []).push(x), m), {})).sort((p, q) => q[1].length - p[1].length);
  const recent = [...docs].sort((p, q) => q.ts - p.ts), shown = recent.slice(0, 3);
  const toggle = (t) => setOpen((s) => { const n = new Set(s); n.has(t) ? n.delete(t) : n.add(t); return n; });
  return (
    <div data-testid={`er-docs-${d.id}`}>
      {all ? (
        groups.map(([t, list]) => (
          <div key={t} className="border-t first:border-t-0" style={{ borderColor: HAIR }}>
            <button type="button" onClick={() => toggle(t)} className="w-full flex items-center gap-1.5 py-1.5 text-left text-[12.5px] font-semibold" style={{ color: INK }} aria-expanded={open.has(t)} data-testid={`er-docs-type-${t.replace(/\s+/g, "-").toLowerCase()}`}>
              <ChevronRight size={13} style={{ color: FAINT, transform: open.has(t) ? "rotate(90deg)" : "none", transition: "transform .15s" }} />
              <span className="flex-1 truncate">{t}</span><span className="text-[11px] font-bold px-1.5 rounded-full" style={{ background: "#EFEFF1", color: SUB }}>{list.length}</span>
            </button>
            {open.has(t) ? <div className="pl-4 pb-1">{[...list].sort((p, q) => q.ts - p.ts).map((x) => <Row key={x.key} x={x} onOpen={onOpen} />)}</div> : null}
          </div>
        ))
      ) : (
        shown.map((x) => <Row key={x.key} x={x} onOpen={onOpen} />)
      )}
      {docs.length > 3 ? <button type="button" onClick={() => setAll(!all)} className="text-[12px] font-semibold mt-1" style={{ color: "#4A4AC2" }} data-testid={`er-docs-recent-more-${d.id}`}>{all ? "Show the 3 most recent" : `Show all ${docs.length}`}</button> : null}
      <Viewer doc={doc} onClose={() => setDoc(null)} />
    </div>
  );
}
